import { Paper, Grid2 as Grid, Select, MenuItem, styled, Autocomplete, TextField, Chip, Popper, Tooltip, Button } from "@mui/material";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import DateTimeRangeSelect from "../../components/input/date/DateTimeRangeSelect";
import FormInput from "../../components/input/form/FormInput";
import { StateAction } from "../../utils/typeAlias";
import IEmployee from "../../models/employee";
import { IPropertyGroup } from "../../models/propertyGroup";
import { IRoute } from "../../models/route";

const CHIPS_LIMIT_FOR_AUTOCOMPLETE = 2;

const CenterGrid = styled(Grid)(() => ({
  display: "flex",
  alignItems: "center",
}));
interface SearchProps {
  propertyGroups: IPropertyGroup[];
  setSelectedPropertyGroups: StateAction<number[]>;
  selectedPropertyGroups: number[]
  start: Date;
  setStart: StateAction<Date>;
  end: Date | undefined;
  setEnd: StateAction<Date | undefined>;
  selectedEmployees: number[];
  setSelectedEmployees: StateAction<number[]>;
  employees: IEmployee[];
  isFilteredByRoute?: boolean;
  routes?: IRoute[];
  selectedRoutes?: number[];
  setSelectedRoutes?: StateAction<number[]>;
  isEmployeeMode?: boolean;
}

const TimesheetSearchBar = ({
  start,
  setStart,
  end,
  setEnd,
  propertyGroups,
  selectedPropertyGroups,
  setSelectedPropertyGroups,
  selectedEmployees,
  setSelectedEmployees,
  employees,
  isFilteredByRoute = false,
  selectedRoutes,
  setSelectedRoutes,
  routes,
  isEmployeeMode = false,

}: SearchProps) => {
  const { t } = useTranslation();
  const [selectedStart, setSelectedStart] = useState<Date>(start)
  const [selectedEnd, setSelectedEnd] = useState<Date | undefined>(end)

  const onRefreshFilter = () => {
    setStart(selectedStart)
    setEnd(selectedEnd)
  }

  const updatePropertyGroup = (value: number[]): void => {
    const ids = value.map((v) => Number(v));
    setSelectedPropertyGroups(ids);
  };

  const updateEmployee = (value: number[]): void => {
    const ids = value.map((v) => Number(v));
    setSelectedEmployees(ids);
  };

  const updateRoutes = (value: string | number[]): void => {
    const ids =
      typeof value === "string"
        ? value.split(",").map((v) => Number(v))
        : value;
    setSelectedRoutes && setSelectedRoutes(ids);
  };


  const renderEmployeeSelect = () => {
    return (
      <CenterGrid size={{ xs: 12, sm: 5, md: 3 }}>
        <FormInput
          sx={{ width: "100%" }}
          label={t("header.employees")}
        >
          <Autocomplete
            fullWidth
            multiple
            disableClearable={isEmployeeMode}
            options={employees}
            getOptionLabel={(option) => option.name}
            value={employees.filter((employee) => selectedEmployees.includes(employee.id))}
            onChange={(_, newValue) => {
              if (Array.isArray(newValue) && (!isEmployeeMode || newValue.length > 0)) {
                updateEmployee(newValue.map((val) => val.id));
              }
            }}
            renderInput={(params) => (
              <TextField {...params} />
            )}
            disabled={employees.length <= 0}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderTags={(value, getTagProps) => {
              return (
                <>
                  {value.slice(0, CHIPS_LIMIT_FOR_AUTOCOMPLETE).map((option, index) => (
                    <Chip
                      label={option.name}
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))}
                  {value.length > CHIPS_LIMIT_FOR_AUTOCOMPLETE && (
                    <Tooltip title={value.slice(CHIPS_LIMIT_FOR_AUTOCOMPLETE).map((v) => v.name).join(', ')}>
                      <Chip label={`+${value.length - CHIPS_LIMIT_FOR_AUTOCOMPLETE}`} />
                    </Tooltip>
                  )}
                </>
              );
            }}
          />
        </FormInput>
      </CenterGrid>
    );
  };

  return (
    <Paper elevation={1} sx={{ padding: 2 }}>
      <Grid container display={"flex"} justifyContent="space-around" rowSpacing={2} columnSpacing={{ xs: 2, md: 0 }}>
        <Grid size={{ xs: 12, sm: 5, md: 3 }} alignSelf={"center"}>
          <DateTimeRangeSelect
            minDate={moment("2023-01-01").startOf("day").toDate()}
            sameDaySelection
            disableFuture
            start={selectedStart}
            setStart={setSelectedStart}
            end={selectedEnd}
            setEnd={setSelectedEnd}
            labelName={""}
          />
        </Grid>

        {isEmployeeMode && renderEmployeeSelect()}

        <CenterGrid size={{ xs: 12, sm: 5, md: 3 }}>
          <FormInput
            sx={{ width: "100%" }}
            label={t("table.property.propertyGroups")}
          >
            <Autocomplete
              fullWidth
              multiple
              disableClearable={!isEmployeeMode}
              options={propertyGroups}
              getOptionLabel={(option) => option.name}
              value={propertyGroups.filter((group) => selectedPropertyGroups.includes(group.id))}
              onChange={(_, newValue) => {
                if (Array.isArray(newValue) && (isEmployeeMode || newValue.length > 0)) {
                  updatePropertyGroup(newValue.map((val) => val.id));
                }
              }}
              renderInput={(params) => (
                <TextField {...params} />
              )}
              disabled={propertyGroups.length <= 0}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderTags={(value, getTagProps) => {
                return (
                  <>
                    {value.slice(0, CHIPS_LIMIT_FOR_AUTOCOMPLETE).map((option, index) => (
                      <Chip
                        label={option.name}
                        {...getTagProps({ index })}
                        key={option.id}
                      />
                    ))}
                    {value.length > CHIPS_LIMIT_FOR_AUTOCOMPLETE && (
                      <Tooltip title={value.slice(CHIPS_LIMIT_FOR_AUTOCOMPLETE).map((v) => v.name).join(', ')}>
                        <Chip label={`+${value.length - CHIPS_LIMIT_FOR_AUTOCOMPLETE}`} />
                      </Tooltip>
                    )}
                  </>
                );
              }}
            />
          </FormInput>
        </CenterGrid>

        {isFilteredByRoute && (<CenterGrid size={{ xs: 12, sm: 5, md: 2 }}>
          <FormInput
            sx={{ width: "100%" }}
            label="Route"
          >
            <Select
              notched
              multiple
              fullWidth
              disabled={employees.length <= 0}
              defaultValue={[-1]}
              value={selectedRoutes}
              onChange={(event) =>
                updateRoutes(event.target.value)
              }
              id="route-select"
            >
              {routes && routes.map((route) => (
                <MenuItem
                  key={`route_${route.id}`}
                  value={route.id}
                >
                  {route.name}
                </MenuItem>
              ))}
            </Select>
          </FormInput>
        </CenterGrid>
        )}

        {!isEmployeeMode && renderEmployeeSelect()}

        <Grid size={{ xs: 12, sm: 5, md: 4 }} offset={{ md: 'auto' }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            disabled={
              selectedStart.toString() == start.toString() && selectedEnd?.toString() == end?.toString()
            }
            sx={{
              marginRight: "1em",
            }}
            onClick={() => onRefreshFilter()}
          >
            {t("header.applyFilters")}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default TimesheetSearchBar;
